import React, { useState, useRef, useEffect } from 'react';
import BaseButton from './Button/BaseButton';
import './css/ExpanderPanel.css';

interface ExpanderPanelProps {
    id?: string,
    iconPath?: string;
    iconWidth?: number;
    iconHeight?: number;
    iconViewBox?: string;
    iconComponent?: React.ReactNode;
    title: string;
    children?: React.ReactNode;
    openIndicatorPath?: string;
    openIndicatorWidth?: number;
    openIndicatorHeight?: number;
    openIndicatorViewBox?: string;
    expanded?: boolean;              // controlled mode
    defaultExpanded?: boolean;       // uncontrolled mode
    onStateChanging?: (nextExpanded: boolean) => void;
    onStateChanged?: (expanded: boolean) => void;
    indicatorPosition?: 'left' | 'right';
    className?: string;
}

export const ExpanderPanel: React.FC<ExpanderPanelProps> = ({
    id,
    iconPath,
    iconWidth = 24,
    iconHeight = 24,
    iconViewBox = "0 0 32 32",
    iconComponent,
    title,
    children,
    openIndicatorPath = 'M0,0L32,0 16,19.745z',   // default down arrow
    openIndicatorWidth = "24",
    openIndicatorHeight = "14.81",
    openIndicatorViewBox = "0 0 32 19.747",
    expanded,
    defaultExpanded = false,
    onStateChanging,
    onStateChanged,
    indicatorPosition = 'right',
    className = '',
}) => {
    const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
    const isControlled = expanded !== undefined;
    const isExpanded = isControlled ? expanded! : internalExpanded;

    const bodyRef = useRef<HTMLDivElement>(null);
    const bodyContentRef = useRef<HTMLDivElement>(null);

    // Handle toggle
    const toggle = () => {
        const next = !isExpanded;
        onStateChanging?.(next);
        if (!isControlled) setInternalExpanded(next);
    };

    // Fire StateChanged after animation completes
    useEffect(() => {
        if (bodyRef.current && bodyContentRef.current) {
            const handleBodyTransitionEnd = () => {

                console.log(bodyRef.current?.style.maxHeight);

                if (bodyRef.current?.offsetHeight === 0)
                    onStateChanged?.(false); // when max height has transitioned to 0
            };
            const handleBodyContentTransitionEnd = () => {

                const opacityValue = parseFloat(window.getComputedStyle(bodyContentRef.current!).opacity);

                if (opacityValue === 1) {
                    onStateChanged?.(true); // when opacity has transitioned to 1
                }
            };
            bodyRef.current.addEventListener('transitionend', handleBodyTransitionEnd);
            bodyContentRef.current.addEventListener('transitionend', handleBodyContentTransitionEnd);
            return () => {
                bodyRef.current?.removeEventListener('transitionend', handleBodyTransitionEnd);
                bodyContentRef.current?.removeEventListener('transitionend', handleBodyContentTransitionEnd);
            };
        }
    }, [isExpanded, onStateChanged]);

    return (
        <div id={id} className={`expander-panel ${className}`}>
            <BaseButton
                ariaLabel={title}
                className="expander-header"
                onClick={toggle}>
                <span className="expander-header-content">
                    {iconComponent}
                    <span className="expander-title">{title}</span>
                    {indicatorPosition === 'right' && (
                        <svg
                            className={`expander-indicator ${isExpanded ? 'expanded' : 'collapsed'}`}
                            width={openIndicatorWidth}
                            height={openIndicatorHeight}
                            viewBox={openIndicatorViewBox}
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true">
                            <path d={openIndicatorPath} fill="currentColor" />
                        </svg>
                    )}
                </span>
            </BaseButton>

            <div ref={bodyRef}
                className={`expander-body ${isExpanded ? 'expanded' : 'collapsed'}`}>
                <div ref={bodyContentRef}
                    className={`expander-body-content ${isExpanded ? 'expanded' : 'collapsed'}`}>
                    {children}
                </div>
            </div>
        </div>
    );
};

export default ExpanderPanel;
